import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import {
  AvailableCompany,
  fetchAvailableCompanies,
  fetchMe,
  login as apiLogin,
  logout as apiLogout,
  MeResponse,
} from "../api/auth";
import {
  AUTH_REFRESH_FAILED_EVENT,
  clearAuthTokens,
  refreshAuthTokens,
} from "../api/axios";

type AuthState = {
  user: MeResponse | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  hasRole: (...roles: MeResponse["role"][]) => boolean;
  company_id: number | null;
  availableCompanies: AvailableCompany[];
  selectedCompanyId: number | null;
  setSelectedCompanyId: (companyId: number | null) => void;
};

const AuthContext = createContext<AuthState | null>(null);
const LS_KEY = "auth.user.v1";
const AUTH_ERROR_MESSAGE = "Ошибка авторизации, обновите страницу";

const selectedCompanyStorageKey = (userId: number) => `auth.selected_company_id.v1.${userId}`;

const normalizeCompaniesFromMe = (me: MeResponse | null): AvailableCompany[] => {
  if (!me) return [];
  if (Array.isArray(me.accessible_companies) && me.accessible_companies.length > 0) {
    return me.accessible_companies;
  }
  if (typeof me.company_id === "number") {
    return [
      {
        id: me.company_id,
        company_name: me.company_name || "Компания",
      },
    ];
  }
  return [];
};

const getUrlToken = () => {
  if (typeof window === "undefined") return null;
  const publicTokenPaths = new Set([
    "/confirm-account",
    "/auth/confirm-account",
    "/reset-password",
    "/auth/reset-password",
  ]);
  if (publicTokenPaths.has(window.location.pathname)) return null;
  const params = new URLSearchParams(window.location.search);
  return params.get("token");
};

const removeTokenFromUrl = () => {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  url.searchParams.delete("token");
  const nextUrl = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, document.title, nextUrl);
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<MeResponse | null>(null);
  const [availableCompanies, setAvailableCompanies] = useState<AvailableCompany[]>([]);
  const [selectedCompanyId, setSelectedCompanyIdState] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState("");

  const refreshUser = useCallback(async () => {
    try {
      const me = await fetchMe();
      setUser(me);
      setAvailableCompanies(normalizeCompaniesFromMe(me));
      localStorage.setItem(LS_KEY, JSON.stringify(me));
      try {
        const companies = await fetchAvailableCompanies();
        if (Array.isArray(companies) && companies.length > 0) {
          setAvailableCompanies(companies);
        }
      } catch {}
    } catch (error) {
      setUser(null);
      setAvailableCompanies([]);
      setSelectedCompanyIdState(null);
      localStorage.removeItem(LS_KEY);
      throw error;
    }
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const cachedUser = JSON.parse(raw);
        setUser(cachedUser);
        setAvailableCompanies(normalizeCompaniesFromMe(cachedUser));
      }
    } catch {}
    (async () => {
      const urlToken = getUrlToken();
      try {
        if (urlToken) {
          removeTokenFromUrl();
          await refreshAuthTokens(urlToken);
        }
        await refreshUser();
      } catch (error) {
        setUser(null);
        setAvailableCompanies([]);
        setSelectedCompanyIdState(null);
        localStorage.removeItem(LS_KEY);
        if (getUrlToken()) {
          removeTokenFromUrl();
        }
        if (urlToken && error) setAuthError(AUTH_ERROR_MESSAGE);
      } finally {
        setLoading(false);
      }
    })();
  }, [refreshUser]);

  useEffect(() => {
    const handleRefreshFailed = () => {
      setUser(null);
      setAvailableCompanies([]);
      setSelectedCompanyIdState(null);
      localStorage.removeItem(LS_KEY);
      setAuthError(AUTH_ERROR_MESSAGE);
    };

    window.addEventListener(AUTH_REFRESH_FAILED_EVENT, handleRefreshFailed);
    return () => window.removeEventListener(AUTH_REFRESH_FAILED_EVENT, handleRefreshFailed);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    clearAuthTokens();
    setAuthError("");
    await apiLogin(email, password);
    await refreshUser();
  }, [refreshUser]);

  const logout = useCallback(async () => {
    try { await apiLogout(); } catch {}
    clearAuthTokens();
    setUser(null);
    setAvailableCompanies([]);
    setSelectedCompanyIdState(null);
    localStorage.removeItem(LS_KEY);
    window.location.href = "/auth/login";
  }, []);

  const hasRole = useCallback((...roles: MeResponse["role"][]) => !!user && roles.includes(user.role), [user]);
  const setSelectedCompanyId = useCallback((companyId: number | null) => {
    setSelectedCompanyIdState(companyId);
    if (!user) return;

    const storageKey = selectedCompanyStorageKey(user.id);
    if (companyId) {
      sessionStorage.setItem(storageKey, String(companyId));
    } else {
      sessionStorage.removeItem(storageKey);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    if (availableCompanies.length === 0) {
      setSelectedCompanyIdState(null);
      return;
    }

    const isCurrentAllowed = selectedCompanyId !== null && availableCompanies.some((company) => company.id === selectedCompanyId);
    if (isCurrentAllowed) return;

    const savedValue = sessionStorage.getItem(selectedCompanyStorageKey(user.id));
    const savedCompanyId = savedValue ? Number(savedValue) : null;
    const isSavedAllowed = savedCompanyId !== null && availableCompanies.some((company) => company.id === savedCompanyId);
    const nextCompanyId = isSavedAllowed ? savedCompanyId : availableCompanies[0].id;

    setSelectedCompanyIdState(nextCompanyId);
    sessionStorage.setItem(selectedCompanyStorageKey(user.id), String(nextCompanyId));
  }, [availableCompanies, selectedCompanyId, user]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      login,
      logout,
      refreshUser,
      hasRole,
      company_id: selectedCompanyId ?? user?.company_id ?? null,
      availableCompanies,
      selectedCompanyId: selectedCompanyId ?? user?.company_id ?? null,
      setSelectedCompanyId,
    }),
    [availableCompanies, hasRole, loading, login, logout, refreshUser, selectedCompanyId, setSelectedCompanyId, user]
  );

  if (authError) {
    return (
      <div className="min-h-screen bg-slate-50 px-6 py-10 text-slate-900">
        <div className="mx-auto max-w-xl rounded-2xl border border-rose-200 bg-white px-6 py-5 text-sm text-rose-700 shadow-sm">
          {authError}
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
